<template>
  <div class="ad-page">
    <div class="ad-header">
      <h1 class="ad-title">系統概覽</h1>
      <p class="ad-date">{{ todayLabel }}</p>
    </div>

    <div v-if="loading" class="ad-loading">
      <span class="ad-spinner" />
      <p>載入統計資料…</p>
    </div>
    <div v-else-if="loadError" class="ad-error">
      <p>{{ loadError }}</p>
      <button class="ad-btn ad-btn--secondary" @click="load">重新載入</button>
    </div>

    <template v-else>
      <div class="ad-stats">
        <router-link to="/admin/booking-stores" class="ad-stat-card ad-stat-card--link">
          <p class="ad-stat-card__label">上線店家</p>
          <p class="ad-stat-card__value">{{ stats.activeStores }}</p>
          <p class="ad-stat-card__hint">點擊管理 →</p>
        </router-link>
        <router-link
          to="/admin/booking-stores"
          class="ad-stat-card ad-stat-card--warn ad-stat-card--link"
        >
          <p class="ad-stat-card__label">待審核申請</p>
          <p class="ad-stat-card__value">{{ stats.pendingStores }}</p>
          <p class="ad-stat-card__hint">點擊審核 →</p>
        </router-link>
        <router-link to="/admin/booking-reservations" class="ad-stat-card ad-stat-card--info ad-stat-card--link">
          <p class="ad-stat-card__label">本週預約</p>
          <p class="ad-stat-card__value">{{ stats.weekRes }}</p>
          <p class="ad-stat-card__hint">點擊查看 →</p>
        </router-link>
        <router-link to="/admin/booking-reservations" class="ad-stat-card ad-stat-card--success ad-stat-card--link">
          <p class="ad-stat-card__label">本月預約</p>
          <p class="ad-stat-card__value">{{ stats.monthRes }}</p>
          <p class="ad-stat-card__hint">點擊查看 →</p>
        </router-link>
      </div>

      <div class="ad-quicklinks">
        <h2 class="ad-quicklinks__title">快速導覽</h2>
        <div class="ad-quicklinks__grid">
          <router-link to="/admin/booking-stores" class="ad-ql-card">
            <span class="ad-ql-card__icon">🏪</span>
            <span>店家管理</span>
          </router-link>
          <router-link to="/admin/booking-reservations" class="ad-ql-card">
            <span class="ad-ql-card__icon">📅</span>
            <span>預約總覽</span>
          </router-link>
          <router-link to="/admin/users" class="ad-ql-card">
            <span class="ad-ql-card__icon">👥</span>
            <span>會員管理</span>
          </router-link>
          <router-link to="/admin/orders" class="ad-ql-card">
            <span class="ad-ql-card__icon">🛒</span>
            <span>訂單管理</span>
          </router-link>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import axios from 'axios'
import { useRouter } from 'vue-router'

const router = useRouter()
const loading   = ref(false)
const loadError = ref('')
const stats = ref({ activeStores: 0, pendingStores: 0, weekRes: 0, monthRes: 0 })

const todayLabel = new Date().toLocaleDateString('zh-TW', {
  timeZone: 'Asia/Taipei',
  year: 'numeric', month: 'long', day: 'numeric', weekday: 'long',
})

async function load() {
  loading.value   = true
  loadError.value = ''
  try {
    const { data } = await axios.get('/api/admin/booking/dashboard')
    if (data.success) stats.value = data
  } catch (e) {
    if (e.response?.status === 401) { router.push({ name: 'AdminLogin' }); return }
    loadError.value = '載入失敗，請重新整理'
  } finally {
    loading.value = false
  }
}

onMounted(load)
</script>

<style scoped>
.ad-page { max-width: 960px; }

.ad-header {
  display: flex; justify-content: space-between; align-items: flex-end;
  margin-bottom: 28px; flex-wrap: wrap; gap: 8px;
}
.ad-title { font-size: 1.6rem; color: #2c3e50; margin: 0; }
.ad-date  { font-size: 0.88rem; color: #95a5a6; margin: 0; }

.ad-loading {
  display: flex; flex-direction: column; align-items: center;
  gap: 12px; padding: 60px; color: #aaa;
}
.ad-error { text-align: center; padding: 48px; color: #c0392b; }
.ad-spinner {
  display: inline-block; width: 28px; height: 28px;
  border: 3px solid #ddd; border-top-color: #2E3A59;
  border-radius: 50%; animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.ad-stats {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 16px; margin-bottom: 36px;
}
.ad-stat-card {
  background: #fff; border-radius: 12px; padding: 24px 20px 18px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.07); border-left: 4px solid #dce1e7;
  text-decoration: none; display: block; color: inherit;
}
.ad-stat-card--link {
  transition: box-shadow 0.18s, transform 0.18s; cursor: pointer;
}
.ad-stat-card--link:hover {
  box-shadow: 0 4px 16px rgba(0,0,0,0.12); transform: translateY(-2px);
}
.ad-stat-card--warn    { border-left-color: #f39c12; }
.ad-stat-card--info    { border-left-color: #3498db; }
.ad-stat-card--success { border-left-color: #27ae60; }
.ad-stat-card__label {
  font-size: 0.78rem; color: #7f8c8d;
  margin: 0 0 8px; text-transform: uppercase; letter-spacing: 0.04em;
}
.ad-stat-card__value {
  font-size: 2.4rem; font-weight: 700; color: #2c3e50;
  margin: 0 0 6px; line-height: 1;
}
.ad-stat-card__hint { font-size: 0.75rem; color: #bdc3c7; margin: 0; }

.ad-quicklinks { margin-top: 8px; }
.ad-quicklinks__title {
  font-size: 0.95rem; color: #7f8c8d; margin: 0 0 14px;
  text-transform: uppercase; letter-spacing: 0.05em;
}
.ad-quicklinks__grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
}
.ad-ql-card {
  background: #fff; border-radius: 10px; padding: 20px 16px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  text-decoration: none; color: #2c3e50;
  display: flex; flex-direction: column; align-items: center;
  gap: 10px; font-size: 0.88rem; font-weight: 600;
  border: 1px solid #ede7e2;
  transition: box-shadow 0.18s, transform 0.18s;
}
.ad-ql-card:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.1); transform: translateY(-2px);
}
.ad-ql-card__icon { font-size: 1.6rem; }

.ad-btn--secondary {
  background: transparent; border: 1px solid #bdc3c7; color: #7f8c8d;
  font-size: 0.9rem; padding: 8px 18px; border-radius: 7px; cursor: pointer;
}
.ad-btn--secondary:hover { background: #f4f6f9; }

@media (max-width: 600px) {
  .ad-stats { grid-template-columns: repeat(2, 1fr); }
  .ad-stat-card__value { font-size: 1.8rem; }
  .ad-quicklinks__grid { grid-template-columns: repeat(2, 1fr); }
}
</style>
